import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { userHasAnyRole } from "../../../services/authService";
import {
  ClinicalMapRegion,
  ClinicalMapType,
  createClinicalMapRegion,
  deactivateClinicalMapRegion,
  listClinicalMapRegions,
  updateClinicalMapRegion,
} from "../../../services/clinicalMapService";

const ClinicalMapRegionsPage = () => {
  const { t } = useTranslation();
  const canManage = userHasAnyRole(["owner", "manager", "receptionist"]);

  document.title = t("settings.clinical_map.pageTitle");

  const [mapType, setMapType] = useState<ClinicalMapType>("face");
  const [regions, setRegions] = useState<ClinicalMapRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ClinicalMapRegion | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<ClinicalMapRegion | null>(null);
  const [name, setName] = useState("");
  const [hotspotX, setHotspotX] = useState("");
  const [hotspotY, setHotspotY] = useState("");
  const [sortOrder, setSortOrder] = useState("500");

  const fetchRegions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listClinicalMapRegions({
        map_type: mapType,
        include_inactive: true,
      });
      setRegions(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_map.toast.loadError"));
      setRegions([]);
    } finally {
      setLoading(false);
    }
  }, [mapType, t]);

  useEffect(() => {
    void fetchRegions();
  }, [fetchRegions]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setHotspotX("");
    setHotspotY("");
    setSortOrder("500");
    setModalOpen(true);
  };

  const openEdit = (region: ClinicalMapRegion) => {
    setEditing(region);
    setName(region.name);
    setHotspotX(region.hotspot_x_pct != null ? String(region.hotspot_x_pct) : "");
    setHotspotY(region.hotspot_y_pct != null ? String(region.hotspot_y_pct) : "");
    setSortOrder(String(region.sort_order));
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t("settings.clinical_map.validation.nameRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const hotspot_x_pct = hotspotX === "" ? null : Number(hotspotX);
      const hotspot_y_pct = hotspotY === "" ? null : Number(hotspotY);
      if (editing) {
        await updateClinicalMapRegion(editing.id, {
          name: name.trim(),
          sort_order: Number(sortOrder) || 0,
          hotspot_x_pct,
          hotspot_y_pct,
        });
        toast.success(t("settings.clinical_map.toast.updated"));
      } else {
        await createClinicalMapRegion({
          map_type: mapType,
          name: name.trim(),
          sort_order: Number(sortOrder) || 500,
          hotspot_x_pct,
          hotspot_y_pct,
        });
        toast.success(t("settings.clinical_map.toast.created"));
      }
      setModalOpen(false);
      await fetchRegions();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_map.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) {
      return;
    }
    setSubmitting(true);
    try {
      await deactivateClinicalMapRegion(deactivateTarget.id);
      toast.success(t("settings.clinical_map.toast.deactivated"));
      setDeactivateTarget(null);
      await fetchRegions();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_map.toast.deactivateError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async (region: ClinicalMapRegion) => {
    setSubmitting(true);
    try {
      await updateClinicalMapRegion(region.id, { is_active: true });
      toast.success(t("settings.clinical_map.toast.reactivated"));
      await fetchRegions();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_map.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.clinical_map.breadcrumb.title")}
          pageTitle={t("settings.clinical_map.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h5 className="card-title mb-1">
                    {t("settings.clinical_map.card.title")}
                  </h5>
                  <p className="text-muted mb-0 small">
                    {t("settings.clinical_map.card.subtitle")}
                  </p>
                </div>
                {canManage ? (
                  <Button color="primary" onClick={openCreate}>
                    {t("settings.clinical_map.actions.new")}
                  </Button>
                ) : null}
              </CardHeader>
              <CardBody>
                <Nav tabs className="mb-3">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={mapType === "face" ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        setMapType("face");
                      }}
                    >
                      {t("settings.clinical_map.face")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={mapType === "body" ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        setMapType("body");
                      }}
                    >
                      {t("settings.clinical_map.body")}
                    </NavLink>
                  </NavItem>
                </Nav>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : regions.length === 0 ? (
                  <p className="text-muted mb-0">
                    {t("settings.clinical_map.empty")}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("settings.clinical_map.table.name")}</th>
                          <th>{t("settings.clinical_map.table.code")}</th>
                          <th>{t("settings.clinical_map.table.hotspot")}</th>
                          <th>{t("settings.clinical_map.table.status")}</th>
                          <th className="text-end">
                            {t("settings.clinical_map.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {regions.map((region) => (
                          <tr key={region.id}>
                            <td>
                              {region.name}
                              {region.is_system ? (
                                <Badge color="soft-info" className="text-info ms-2">
                                  {t("settings.clinical_map.system")}
                                </Badge>
                              ) : null}
                            </td>
                            <td>
                              <code>{region.code}</code>
                            </td>
                            <td>
                              {region.has_hotspot
                                ? `${region.hotspot_x_pct}%, ${region.hotspot_y_pct}%`
                                : "—"}
                            </td>
                            <td>
                              <Badge color={region.is_active ? "success" : "secondary"}>
                                {region.is_active
                                  ? t("settings.clinical_map.status.active")
                                  : t("settings.clinical_map.status.inactive")}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {canManage ? (
                                <div className="d-inline-flex gap-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => openEdit(region)}
                                  >
                                    {t("settings.clinical_map.actions.edit")}
                                  </Button>
                                  {region.is_active ? (
                                    <Button
                                      color="soft-danger"
                                      size="sm"
                                      onClick={() => setDeactivateTarget(region)}
                                    >
                                      {t("settings.clinical_map.actions.deactivate")}
                                    </Button>
                                  ) : (
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      disabled={submitting}
                                      onClick={() => void handleReactivate(region)}
                                    >
                                      {t("settings.clinical_map.actions.reactivate")}
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editing
            ? t("settings.clinical_map.form.editTitle")
            : t("settings.clinical_map.form.createTitle")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("settings.clinical_map.form.name")}</Label>
            <Input
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{t("settings.clinical_map.form.hotspotX")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={hotspotX}
                  onChange={(e) => setHotspotX(e.target.value)}
                  placeholder="0–100"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>{t("settings.clinical_map.form.hotspotY")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={hotspotY}
                  onChange={(e) => setHotspotY(e.target.value)}
                  placeholder="0–100"
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup className="mb-0">
            <Label>{t("settings.clinical_map.form.sortOrder")}</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
            <p className="text-muted small mt-1 mb-0">
              {t("settings.clinical_map.form.hotspotHint")}
            </p>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalOpen(false)}>
            {t("settings.clinical_map.actions.cancel")}
          </Button>
          <Button color="primary" disabled={submitting} onClick={() => void handleSave()}>
            {t("settings.clinical_map.actions.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={deactivateTarget !== null}
        toggle={() => setDeactivateTarget(null)}
        centered
      >
        <ModalHeader toggle={() => setDeactivateTarget(null)}>
          {t("settings.clinical_map.deactivate.title")}
        </ModalHeader>
        <ModalBody>
          {t("settings.clinical_map.deactivate.description", {
            name: deactivateTarget?.name ?? "",
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeactivateTarget(null)}>
            {t("settings.clinical_map.actions.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={submitting}
            onClick={() => void handleDeactivate()}
          >
            {t("settings.clinical_map.actions.confirmDeactivate")}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default ClinicalMapRegionsPage;
